import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, MapPin, Calendar, Clock, Users, BookOpen, Building, Sparkles, Briefcase } from 'lucide-react';
import { volunteerOpportunities } from '../../data/mockData';
import { centerToast } from '../../components/CenterToast';

const CATEGORY_STYLE = {
  'Community Outreach': { icon: Users,     bg: 'bg-primary',    text: 'text-primary-foreground' },
  'Community':          { icon: Sparkles,  bg: 'bg-teal-500',   text: 'text-white' },
  'Education':          { icon: BookOpen,  bg: 'bg-indigo-900', text: 'text-white' },
  'Corporate':          { icon: Building,  bg: 'bg-amber-500',  text: 'text-white' }
};

const CATEGORIES = ['All', 'Community Outreach', 'Community', 'Education', 'Corporate'];

export default function Serve() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [signedUp, setSignedUp] = useState(new Set());

  const filteredOpportunities = volunteerOpportunities.filter((opp) => {
    const matchesCategory = selectedCategory === 'All' || opp.category === selectedCategory;
    const matchesSearch = opp.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleSignUp = (oppId, oppTitle) => {
    if (signedUp.has(oppId)) {
      setSignedUp((prev) => {
        const newSet = new Set(prev);
        newSet.delete(oppId);
        return newSet;
      });
      centerToast(`Removed from "${oppTitle}"`, '👋');
    } else {
      setSignedUp((prev) => new Set([...prev, oppId]));
      centerToast(`Signed up for "${oppTitle}"!`, '🎉');
    }
  };

  const handleExpressInterest = (oppTitle) => {
    centerToast(`Interest recorded for "${oppTitle}". Our team will be in touch.`, '🤝');
  };

  return (
    <div className="min-h-screen bg-background page-fade-in">
      {/* Header */}
      <div className="bg-card border-b px-4 py-6">
        <h1 className="text-3xl font-bold mb-2">Serve</h1>
        <p className="text-muted-foreground">Volunteer with MTFA — every hour counts</p>
      </div>

      {/* Search Bar */}
      <div className="px-4 py-4 bg-card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="serve-search"
            placeholder="Search opportunities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11"
          />
        </div>
      </div>

      {/* Category Filters */}
      <div className="px-4 py-3 bg-card border-b">
        <div className="flex gap-2 overflow-x-auto horizontal-scroll pb-2">
          {CATEGORIES.map((category) => (
            <Button
              key={category}
              data-testid={`serve-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className="whitespace-nowrap"
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Opportunities Grid */}
      <div className="p-4 space-y-4">
        {filteredOpportunities.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-10">
            No opportunities match your search.
          </p>
        )}
        {filteredOpportunities.map((opp) => {
          const isFull = opp.volunteers >= opp.capacity;
          const hasSignedUp = signedUp.has(opp.id);
          const spotsRemaining = opp.capacity - opp.volunteers;
          const style = CATEGORY_STYLE[opp.category] || CATEGORY_STYLE['Community'];
          const Icon = style.icon;

          return (
            <Card key={opp.id} data-testid={`opp-${opp.id}`} className="overflow-hidden card-hover">
              {/* Coloured icon header block */}
              <div className={`relative h-32 flex items-center justify-center ${style.bg}`}>
                <Icon className={`h-16 w-16 ${style.text} opacity-90`} strokeWidth={1.5} />
                <div className="absolute top-3 left-3">
                  <Badge className="bg-white/90 text-foreground hover:bg-white/90">
                    {opp.category}
                  </Badge>
                </div>
                <div className="absolute top-3 right-3 flex gap-2">
                  {opp.corporate && (
                    <Badge
                      data-testid={`corporate-badge-${opp.id}`}
                      className="bg-amber-500 text-white hover:bg-amber-500 gap-1"
                    >
                      <Briefcase className="h-3 w-3" />
                      Corporate CSR
                    </Badge>
                  )}
                  {opp.urgent && (
                    <Badge className="bg-destructive text-destructive-foreground hover:bg-destructive">
                      Urgent
                    </Badge>
                  )}
                </div>
              </div>

              <CardContent className="p-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-lg leading-tight mb-1">{opp.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{opp.description}</p>
                </div>

                {/* Details */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <span>{opp.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4 flex-shrink-0" />
                    <span>{opp.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 flex-shrink-0" />
                    <span>{opp.duration}</span>
                  </div>
                  {!opp.corporate && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4 flex-shrink-0" />
                      <span>{opp.volunteers} / {opp.capacity} volunteers</span>
                    </div>
                  )}
                </div>

                {/* Spots warning (skip for corporate) */}
                {!opp.corporate && !isFull && spotsRemaining <= 5 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <p className="text-sm font-medium text-amber-700">
                      Only {spotsRemaining} spots left!
                    </p>
                  </div>
                )}

                {/* CTA */}
                {opp.corporate ? (
                  <Button
                    className="w-full"
                    size="lg"
                    data-testid={`express-interest-${opp.id}`}
                    onClick={() => handleExpressInterest(opp.title)}
                  >
                    Express Interest
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    size="lg"
                    disabled={isFull && !hasSignedUp}
                    variant={hasSignedUp ? 'outline' : 'default'}
                    data-testid={`signup-${opp.id}`}
                    onClick={() => handleSignUp(opp.id, opp.title)}
                  >
                    {isFull ? 'Full' : hasSignedUp ? 'Cancel Registration' : 'Sign Up'}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
